import { useConfirmDeleteEdpAiSubmission } from '@monorepo/logs/src/hooks/useConfirmDeleteEdpAiSubmission';
import { downloadEdpAiSubmissionFile } from '@monorepo/shared/apiClient/logs';
import { EmptyState } from '@monorepo/shared/componentsV2/EmptyState';
import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { Main } from '@monorepo/shared/componentsV2/Main';
import { ActionMenu } from '@monorepo/shared/componentsV2/menus/ActionMenu';
import { useSkipWidgetLink } from '@monorepo/shared/componentsV2/SkipWidgetLink';
import { Pagination } from '@monorepo/shared/componentsV2/Table/Pagination';
import { Table } from '@monorepo/shared/componentsV2/Table/Table';
import { TableColumn } from '@monorepo/shared/componentsV2/Table/types';
import { useToast } from '@monorepo/shared/contexts';
import { useEdpAiSubmissions } from '@monorepo/shared/hooks/logs/useEdpAiSubmissions';
import { useDynamicLinkOnSameProject } from '@monorepo/shared/hooks/navigation/useLinkOnSame/useLinkOnSameProject';
import { useHasProjectUpdatePermissions } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import {
  fillHeightAndScrollable,
  verticalLayout,
} from '@monorepo/shared/styles/layout';
import { format } from 'date-fns';
import {
  EdpAiSubmissionRefResponse,
  EdpAiSubmissionType,
} from 'mapistry-shared';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

const StyledMain = styled(Main)`
  ${verticalLayout}
`;

const StyledTable = styled(Table)`
  ${fillHeightAndScrollable}
` as typeof Table;

const submissionTypeLabels: Record<EdpAiSubmissionType, string> = {
  [EdpAiSubmissionType.FILE_UPLOAD]: 'File upload',
  [EdpAiSubmissionType.FILE_UPLOAD_FROM_MOBILE]: 'File upload (mobile)',
  [EdpAiSubmissionType.EMAIL_SUBMISSION]: 'Email submission',
};

function formatFileNames({ files }: EdpAiSubmissionRefResponse) {
  return files.map((file) => file.filename).join(', ');
}

export function LogDocumentsTab() {
  const { logId, organizationId, projectId } = useParams();
  if (!logId || !organizationId || !projectId) {
    throw new Error(
      "LogDocumentsTab was rendered in a page with url that doesn't have logId, organizationId, or projectId",
    );
  }
  const navigate = useNavigate();
  const { getLinkOnSameProject } = useDynamicLinkOnSameProject();
  const canSubmitDataForProject = useHasProjectUpdatePermissions(projectId);
  const { confirmDeleteEdpAiSubmission } = useConfirmDeleteEdpAiSubmission({
    logId,
    organizationId,
    projectId,
  });
  const [page, setPage] = useState(0);

  const { edpAiSubmissions, pagination, isLoading, error } =
    useEdpAiSubmissions({
      organizationId,
      logId,
      projectId,
      requestedPage: page,
      perPage: 50,
    });

  const { showUserFriendlyErrorToast } = useToast();
  useEffect(() => {
    error && showUserFriendlyErrorToast(error, "Couldn't load documents");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const createActionsColumnContent = useCallback(
    (submission: EdpAiSubmissionRefResponse) => (
      <ActionMenu
        altText={`Action menu for ${formatFileNames(submission)}`}
        options={[
          {
            displayName: 'View all entries',
            onClick: () =>
              navigate(
                getLinkOnSameProject(
                  `/logs/${logId}/records/ai-submission/${submission.id}`,
                ),
                {
                  state: {
                    returnPath: getLinkOnSameProject(
                      `/logs/${logId}/documents`,
                    ),
                    isReadOnly: !canSubmitDataForProject,
                  },
                },
              ),
          },
          {
            displayName: 'Download',
            onClick: () => {
              // TODO@edpai: support multiple file downloads
              const file = submission.files[0];
              if (!file) {
                return;
              }
              downloadEdpAiSubmissionFile({
                logId,
                organizationId,
                projectId,
                edpAiSubmissionId: submission.id,
                fileId: file.fileId,
              });
            },
          },
          ...(canSubmitDataForProject
            ? [
                {
                  displayName: 'Delete all entries',
                  color: 'danger' as const,
                  onClick: () => confirmDeleteEdpAiSubmission(submission),
                },
              ]
            : []),
        ]}
      />
    ),
    [
      canSubmitDataForProject,
      confirmDeleteEdpAiSubmission,
      getLinkOnSameProject,
      logId,
      navigate,
      organizationId,
      projectId,
    ],
  );

  const columns: TableColumn<EdpAiSubmissionRefResponse>[] = useMemo(
    () => [
      { id: 'fileName', header: 'File Name', contents: formatFileNames },
      {
        id: 'submissionDate',
        header: 'Submission Date',
        contents: ({ createdAt }) => format(new Date(createdAt), 'P'),
      },
      {
        id: 'submissionType',
        header: 'Submission Type',
        contents: ({ submissionType }) => submissionTypeLabels[submissionType],
      },
      {
        id: 'entryCount',
        header: 'Entries Generated',
        contents: ({ entryCount }) => String(entryCount),
      },
      {
        id: 'actions',
        header: 'Actions',
        align: 'center',
        width: '10%',
        contents: createActionsColumnContent,
      },
    ],
    [createActionsColumnContent],
  );

  const { SkipLink, SkipLinkTarget } = useSkipWidgetLink();

  if (isLoading) return <Loading />;

  return (
    <ErrorBoundary>
      <StyledMain>
        {edpAiSubmissions?.length ? (
          <>
            <SkipLink linkText="Skip to pagination" />
            <StyledTable
              ariaLabel="All documents"
              columns={columns}
              rows={edpAiSubmissions}
            />
            <SkipLinkTarget />
            <Pagination
              onPageChange={setPage}
              page={pagination?.currentPage || 0}
              rowsPerPage={pagination?.perPage}
              totalRowCount={pagination?.fullCount}
            />
          </>
        ) : (
          <EmptyState
            primaryText="There are no documents associated with this Log."
            secondaryText="When data is submitted to this Log using email submission or file upload, it will show up here."
          />
        )}
      </StyledMain>
    </ErrorBoundary>
  );
}
